import { PORTAL_HOSTS, STORAGE_NAMESPACE, TUNABLES } from "../config";
import type { TelemetryEvent } from "./bus";

/**
 * AN1 (D-122): where a front page's visitor came from, for its `page_landed` record, and whether
 * the browser is the studio's own. The portal comes from the page's host; the source is the
 * linking site's host alone (no path, no query: browsers send no more across sites by default);
 * the campaign is a word a link of ours carries (`?utm_source=` or `?ref=`). A team browser is one
 * that once opened an address with `?team=on` (`?team=off` forgets it), so the queries leave the
 * studio's own visits out. Nothing of it is sent without the visits switch (`boot.ts`).
 */
type Landed = Extract<TelemetryEvent, { type: "page_landed" }>;

export type Portal = NonNullable<Landed["portal"]>;

export interface Landing {
  readonly portal: Portal;
  readonly source: string;
  readonly campaign?: string;
}

const TEAM_KEY = `${STORAGE_NAMESPACE}:team`;
/** The prefixes a site serves the same pages under, dropped so that one site reads as one. */
const MIRROR_PREFIX = /^(?:www|m|l|lm|old|mobile)\./;
const CAMPAIGN_LETTERS = /[^a-z0-9._-]/g;

function within(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

/** The portal that served a page on `host`. */
export function portalOf(host: string): Portal {
  const name = host.toLowerCase();
  return PORTAL_HOSTS.find(([suffix]) => within(name, suffix))?.[1] ?? "other";
}

/** The site that linked to the page: "direct" with none, "self" from the page's own host. */
export function sourceOf(referrer: string, host: string): string {
  if (referrer === "") {
    return "direct";
  }
  let from: string;
  try {
    from = new URL(referrer).hostname.toLowerCase();
  } catch {
    return "other";
  }
  if (from === host.toLowerCase()) {
    return "self";
  }
  return from.replace(MIRROR_PREFIX, "").slice(0, TUNABLES.events.maxSourceChars) || "other";
}

/** A campaign's word from the address, in lower case letters, digits and `._-`, or none. */
export function campaignOf(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const word = (params.get("utm_source") ?? params.get("ref") ?? "")
    .toLowerCase()
    .replace(CAMPAIGN_LETTERS, "")
    .slice(0, TUNABLES.events.maxSourceChars);
  return word === "" ? undefined : word;
}

export function landingOf(page: {
  readonly host: string;
  readonly referrer: string;
  readonly search: string;
}): Landing {
  const campaign = campaignOf(page.search);
  return {
    portal: portalOf(page.host),
    source: sourceOf(page.referrer, page.host),
    ...(campaign === undefined ? {} : { campaign }),
  };
}

/** Whether this browser is the studio's: `?team=on` marks it, `?team=off` forgets it. */
export function teamOf(
  search: string,
  store: {
    get(key: string): string | null;
    set(key: string, value: string): void;
    remove(key: string): void;
  },
): boolean {
  const asked = new URLSearchParams(search).get("team");
  if (asked === "on") {
    store.set(TEAM_KEY, "1");
  } else if (asked === "off") {
    store.remove(TEAM_KEY);
  }
  return store.get(TEAM_KEY) === "1";
}
